/**
 * Remove campaign modal
 *
 * Asks whether a campaign leaving the pool keeps its gift match as a standalone match or loses it.
 */

import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Form, Message, Modal } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { i18nKeys } from '../constants';
import { PoolCampaign } from '../types';

interface RemoveCampaignModalProps {
  campaign: PoolCampaign | null;
  errorKeys: string[];
  isRemoving: boolean;
  onCancel: () => void;
  onConfirm: (keepGiftMatching: boolean) => void;
}

const RemoveCampaignModal: React.FC<RemoveCampaignModalProps> = ({
  campaign,
  errorKeys,
  isRemoving,
  onCancel,
  onConfirm
}) => {
  const [keepGiftMatching, setKeepGiftMatching] = useState(true);

  useEffect(() => {
    setKeepGiftMatching(true);
  }, [campaign?.giftMatchingId]);

  return (
    <Modal
      size="small"
      dimmer="inverted"
      closeIcon
      open={!!campaign}
      onClose={onCancel}
    >
      <Modal.Header>
        <h3>
          {I18n.t(i18nKeys.REMOVE.TITLE, {
            campaign: campaign?.campaignName ?? campaign?.campaignId
          })}
        </h3>
      </Modal.Header>
      <Modal.Content>
        <p>
          <Translate value={i18nKeys.REMOVE.BODY} />
        </p>
        <Form>
          <Form.Field>
            <Checkbox
              radio
              id="remove-campaign-keep"
              name="remove-campaign-choice"
              label={I18n.t(i18nKeys.REMOVE.KEEP)}
              checked={keepGiftMatching}
              onChange={() => setKeepGiftMatching(true)}
            />
          </Form.Field>
          <Form.Field>
            <Checkbox
              radio
              id="remove-campaign-delete"
              name="remove-campaign-choice"
              label={I18n.t(i18nKeys.REMOVE.DELETE)}
              checked={!keepGiftMatching}
              onChange={() => setKeepGiftMatching(false)}
            />
          </Form.Field>
        </Form>
        {errorKeys.length > 0 && (
          <Message negative role="alert">
            {errorKeys.map((key) => (
              <p key={key}>{I18n.t(key)}</p>
            ))}
          </Message>
        )}
        <div
          className="button-wrapper align-right"
          style={{ marginTop: '16px' }}
        >
          <Button type="button" onClick={onCancel} disabled={isRemoving}>
            <Translate value={i18nKeys.REMOVE.CANCEL} />
          </Button>
          <Button
            type="button"
            negative
            loading={isRemoving}
            disabled={isRemoving}
            onClick={() => onConfirm(keepGiftMatching)}
          >
            <Translate value={i18nKeys.REMOVE.CONFIRM} />
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default RemoveCampaignModal;
